


import React, { useMemo, useRef } from "react";
import routeCardImg from "../assets/images/maps/route_9999.jpeg";
import routeOverviewImg from "../assets/images/maps/route9999.jpeg";
import route9997Img from "../assets/images/maps/route_9997.png";
import route9998Img from "../assets/images/maps/route_9998.png";
import routeDiagramImg from "../assets/images/maps/Route000.jpeg";
import routeSatImg from "../assets/images/maps/route_sat.jpeg";
import routeRgbImg from "../assets/images/maps/route_rgb.png";
import routeStartImg from "../assets/images/maps/route_start.png";
import routeStart01Img from "../assets/images/maps/route_start01.png";
import section2Img from "../assets/images/maps/section_2.png";

const defaultSlides = [
  {
    id: "route-start",
    title: "Route Start / First Knock",
    src: routeStartImg,
    alt: "Route start and first knock image",
    caption: "Parking and first-knock reference image. Crew parks at the marked start point and begins the route sequence from this location.",
  },
  {
    id: "route-start-01",
    title: "Route Start / Parking View",
    src: routeStart01Img,
    alt: "Route start parking view image",
    caption: "Alternate parking and first-knock reference image for the crew at the marked starting location.",
  },
  {
    id: "section-2",
    title: "Section 2",
    src: section2Img,
    alt: "Section 2 route image",
    caption: "Section 2 route reference image for the daily field plan.",
  },
  {
    id: "route-rgb",
    title: "Route RGB Flow Plan",
    src: routeRgbImg,
    alt: "Route RGB flow plan",
    caption: "Field crew starts at the red dot and follows the marked route sequence for the neighborhood. Use this image as the closest operational route guide.",
  },
  {
    id: "route-diagram",
    title: "Route Execution Plan",
    src: routeDiagramImg,
    alt: "Route execution diagram",
    caption: "Start at red point → follow pink route first → continue through green → finish with blue sequence. Designed for optimized field movement and parking entry.",
  },
  {
    id: "route-9997",
    title: "Route 9997",
    src: route9997Img,
    alt: "Route 9997 map",
    caption: "Additional route map view for field coverage.",
  },
  {
    id: "route-9998",
    title: "Route 9998",
    src: route9998Img,
    alt: "Route 9998 map",
    caption: "Expanded route variation for daily planning.",
  },
  {
    id: "route-sat",
    title: "Route satellite",
    src: routeSatImg,
    alt: "Route satellite image",
    caption: "Satellite route view for closer visual inspection of the area.",
  },
  {
    id: "route-card",
    title: "Route card",
    src: routeCardImg,
    alt: "Route card image",
    caption: "Selected route card for the daily field area.",
  },
  {
    id: "route-overview",
    title: "Route overview",
    src: routeOverviewImg,
    alt: "Wide route overview map",
    caption: "Wide neighborhood map for daily field coverage.",
  },
];

const defaultMetrics = [
  { label: "Residential", value: "792" },
  { label: "Business", value: "18" },
  { label: "Total", value: "810" },
  { label: "Age 25-34", value: "16.1%" },
  { label: "Size", value: "3.68ppl" },
  { label: "Income", value: "$141.20k" },
  { label: "Cost", value: "$200.07" },
];


function Map({
  routeId = "92154-R029",
  slides = defaultSlides,
  metrics = defaultMetrics,
  destination = {
    title: "Start Location",
    street: "1297 Sea Lily Ct.",
    cityStateZip: "San Diego, CA 92154",
    country: "United States",
  },
}) {
  const scrollRef = useRef(null);

  const directionsUrl = useMemo(() => {
    const fullAddress = [destination.street, destination.cityStateZip, destination.country]
      .filter(Boolean)
      .join(", ");

    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
  }, [destination]);


  const scrollSlides = (direction) => {
    if (!scrollRef.current) {
      return;
    }

    scrollRef.current.scrollBy({
      left: direction * 360,
      behavior: "smooth",
    });
  };

  return (
    <section style={styles.sectionShell}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.kicker}>Route intelligence</div>
          <h2 style={styles.title}>Field Route Map</h2>
          <p style={styles.copy}>
            Swipe or scroll through the uploaded route images, review area metrics, and open driving directions for the marked destination.
          </p>
        </div>

        <div style={styles.routePill}>{routeId}</div>
      </div>

      <div style={styles.metricsGrid}>
        {metrics.map((metric) => (
          <div key={metric.label} style={styles.metricCard}>
            <div style={styles.metricLabel}>{metric.label}</div>
            <div style={styles.metricValue}>{metric.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.galleryHeader}>
        <div>
          <h3 style={styles.galleryTitle}>Daily route gallery</h3>
          <p style={styles.galleryCopy}>
            Replace the files in your route image folder any day you want and this gallery will keep the same structure. Click any map image to open a larger zoom view for closer inspection.
          </p>
        </div>

        <div style={styles.galleryButtons}>
          <button type="button" onClick={() => scrollSlides(-1)} style={styles.galleryButton}>
            ←
          </button>
          <button type="button" onClick={() => scrollSlides(1)} style={styles.galleryButton}>
            →
          </button>
        </div>
      </div>

      <div ref={scrollRef} style={styles.galleryTrack}>
        {slides.map((slide) => (
          <article
            key={slide.id}
            style={{
              ...styles.slideCard,
              ...(slide.onClick ? { cursor: "zoom-in" } : {}),
            }}
            onClick={slide.onClick}
            role={slide.onClick ? "button" : undefined}
            tabIndex={slide.onClick ? 0 : undefined}
            onKeyDown={
              slide.onClick
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      slide.onClick();
                    }
                  }
                : undefined
            }
          >
            <div style={styles.imageShell}>
              <img src={slide.src} alt={slide.alt || slide.title} style={styles.image} />
            </div>
            <div style={styles.slideBody}>
              <div style={styles.slideTitle}>{slide.title}</div>
              <div style={styles.slideCaption}>{slide.caption}</div>
            </div>
          </article>
        ))}
      </div>

      <div style={styles.footerGrid}>
        <div style={styles.locationCard}>
          <div style={styles.kicker}>Marked location</div>
          <h3 style={styles.locationTitle}>{destination.title || "Route Destination"}</h3>
          <div style={styles.addressLine}>{destination.street}</div>
          <div style={styles.addressLine}>{destination.cityStateZip}</div>
          <div style={styles.addressLine}>{destination.country}</div>

          <a href={directionsUrl} target="_blank" rel="noreferrer" style={styles.directionsButton}>
            Open driving directions
          </a>
        </div>

        <div style={styles.notesCard}>
          <div style={styles.kicker}>Route notes</div>
          <h3 style={styles.locationTitle}>Area: {routeId}</h3>
          <p style={styles.copy}>
            Use this section as the daily route intelligence block for your canvassing team. The gallery above can hold screenshot cards, route maps, route detail views, and stats panels that you manually swap in your asset folder.
          </p>
        </div>
      </div>

    </section>
  );
}

const styles = {
  sectionShell: {
    marginTop: 28,
    padding: 28,
    borderRadius: 24,
    background: "linear-gradient(180deg, rgba(14, 14, 14, 0.78), rgba(6, 6, 6, 0.86))",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    boxShadow: "0 18px 60px rgba(0, 0, 0, 0.45)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 20,
    flexWrap: "wrap",
  },
  kicker: {
    display: "inline-block",
    marginBottom: 10,
    color: "#ffb347",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  title: {
    margin: 0,
    fontSize: "2rem",
    lineHeight: 1.05,
    color: "#ffffff",
  },
  copy: {
    margin: "10px 0 0",
    maxWidth: 760,
    color: "rgba(255,255,255,0.72)",
    lineHeight: 1.75,
  },
  routePill: {
    padding: "12px 18px",
    borderRadius: 999,
    border: "1px solid rgba(255, 177, 71, 0.24)",
    background: "rgba(255, 140, 26, 0.08)",
    color: "#ffd166",
    fontWeight: 700,
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 14,
    marginTop: 24,
  },
  metricCard: {
    padding: 16,
    borderRadius: 18,
    background: "linear-gradient(180deg, rgba(18, 18, 18, 0.82), rgba(7, 7, 7, 0.74))",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  metricLabel: {
    color: "rgba(255,255,255,0.56)",
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 8,
  },
  metricValue: {
    color: "#ffffff",
    fontSize: "1.5rem",
    fontWeight: 700,
  },
  galleryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginTop: 30,
    flexWrap: "wrap",
  },
  galleryTitle: {
    margin: 0,
    color: "#ffffff",
    fontSize: "1.25rem",
  },
  galleryCopy: {
    margin: "8px 0 0",
    color: "rgba(255,255,255,0.64)",
    lineHeight: 1.65,
  },
  galleryButtons: {
    display: "flex",
    gap: 10,
  },
  galleryButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.04)",
    color: "#ffffff",
    cursor: "pointer",
    fontSize: "1.1rem",
  },
  galleryTrack: {
    display: "flex",
    gap: 18,
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    paddingBottom: 8,
    marginTop: 18,
  },
  slideCard: {
    minWidth: 340,
    maxWidth: 340,
    borderRadius: 22,
    overflow: "hidden",
    background: "linear-gradient(180deg, rgba(18, 18, 18, 0.82), rgba(7, 7, 7, 0.74))",
    border: "1px solid rgba(255,255,255,0.08)",
    scrollSnapAlign: "start",
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
  },
  imageShell: {
    width: "100%",
    height: 240,
    background: "#050505",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  slideBody: {
    padding: 16,
  },
  slideTitle: {
    color: "#ffffff",
    fontWeight: 700,
    marginBottom: 8,
  },
  slideCaption: {
    color: "rgba(255,255,255,0.66)",
    lineHeight: 1.65,
  },
  footerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 18,
    marginTop: 28,
  },
  locationCard: {
    padding: 22,
    borderRadius: 22,
    background: "linear-gradient(180deg, rgba(18, 18, 18, 0.82), rgba(7, 7, 7, 0.74))",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  notesCard: {
    padding: 22,
    borderRadius: 22,
    background: "linear-gradient(180deg, rgba(18, 18, 18, 0.82), rgba(7, 7, 7, 0.74))",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  locationTitle: {
    margin: "0 0 12px",
    color: "#ffffff",
    fontSize: "1.2rem",
  },
  addressLine: {
    color: "rgba(255,255,255,0.76)",
    lineHeight: 1.8,
  },
  directionsButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 18,
    padding: "12px 16px",
    borderRadius: 14,
    textDecoration: "none",
    fontWeight: 700,
    color: "#181818",
    background: "linear-gradient(135deg, #ff8c1a 0%, #ffd166 100%)",
    boxShadow: "0 10px 26px rgba(255, 140, 26, 0.24)",
  },
};

export default Map;